import {
  Box,
  Button, Center, Divider,
  Flex, FormLabel, HStack,
  Spacer, Spinner, Tab, TabList, TabPanel, TabPanels, Tabs, Text as ChakraText,
  useDisclosure,
  VStack, Stack
} from '@chakra-ui/react'
import { Field as FormikField, Form, Formik } from 'formik'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { UrlEndpointsTextField } from '.'
import { colors } from '../../../ui-kit'
import { pushInBrowserHistory } from '../../../utils/mediaLibrary'
import {
  defaultSourceJson,
  getUrlEndpointsFormFields, initialUrlEndpointsFormState,
  UrlEndpointsSchema, UrlEndpointsWithoutURLPrefixSchema
} from '../../../utils/urlEndpoints'
import useToast from '../../hooks/useToast'
import type {
  EndpointFormProps, SourcesResponse,
  UrlEndpointsResponse,
  UrlEndpointFunctionInfo
} from '../../interfaces'
import { MainContentWrapper } from '../../layouts/Base/MainContent'
import PreferenceListItem from '../../layouts/UrlEndpoints/PreferenceListItem'
import { UserContext } from '../../providers'
import FormDirtyPrompt from '../FormDirtyPrompt'
import ActionBar from './ActionBar'
import AddExistingSource from './AddExistingSource'
import DragDropList from './DragDropList'
import ExampleEndpointUsage from './ExampleEndpointUsage'
import { DeleteModal } from './Modals'
import PermissionGuard from '../PermissionGuard'
import { decodeHash, encodeHash, PermissionsEnum } from '../../../utils'
import UrlRewriterForm from './UrlRewriterForm'
import UrlEndpointFunctionSelector from '../UrlEndpoints/UrlEndpointFunctionSelector'
import OriginCacheControlInfo from './OriginCacheControlInfo'
import { isUndefined } from 'lodash'
import { UpgradePrompt } from '../Common'

interface Props {
  sources?: SourcesResponse[]
  urlEndpoint?: UrlEndpointsResponse
  urlEndpointFunctions: UrlEndpointFunctionInfo[]
  loadingUrlEndpoints: boolean
  loadingSources: boolean
  refreshUrlEndpoints(): void
  refreshSources(): void
}

interface AddEndpointProps {
  [preferenceKey: string]: string | number | boolean |undefined
  urlPrefix: string
  desc: string
  urlEndpointFunctionTitle?: string
  urlEndpointFunctionVersion?: number
  enableUrlEndpointFunction?: boolean
}

type UpdateEndpointProps = UrlEndpointsResponse

interface FormProps {
  urlPrefix: string
  desc: string
  preferenceList: string[]
}

export const UrlEndpointsForm = ({
  sources: allSources,
  refreshUrlEndpoints,
  urlEndpoint,
  loadingUrlEndpoints,
  loadingSources,
  urlEndpointFunctions,
}: Props): React.ReactElement => {
  const { user } = useContext(UserContext)
  let { urlEndpointId } = useParams<{ urlEndpointId: string }>()
  urlEndpointId = decodeHash(urlEndpointId) as string
  const toast = useToast()
  const { request } = useFetch<UrlEndpointsResponse>('/api/clients', {
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const routerHistory = useHistory()
  const routerLocation = useLocation<{
    newEndpoint?: boolean
    urlEndpointId?: string
    formValues?: EndpointFormProps
  }>()
  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure()
  const { t } = useTranslation()

  const [sourcesJson, setSourcesJson] = useState<{
    [sourceId: string]: SourcesResponse
  }>({})
  const [initialSourceIds, setInitialSourceIds] = useState<string[]>([])
  const [initialValues, setInitialValues] = useState<EndpointFormProps>(
    initialUrlEndpointsFormState,
  )
  const [tabIndex, setTabIndex] = useState(0)
  const [canAddMoreEndpoints, setCanAddMoreEndpoints] = useState(true)

  const {
    isOpen: addOriginMenuOpen,
    onClose: closeAddOriginMenu,
    onOpen: openAddOriginMenu,
  } = useDisclosure()

  const urlEndpointsFormFields = getUrlEndpointsFormFields(user, t)

  useEffect(() => {
    const updateHandler = () => {
      const { urlEndpointsLength } = window.clientDataStore ?? {};
      if (!user || !urlEndpointsLength) return;
      if (user?.pricingPlan.limits.fixed.endpoints === -1) return;
      if (urlEndpointsLength + 1 > user?.pricingPlan.limits.fixed.endpoints) {
        setCanAddMoreEndpoints(false)
      }
    };

    // Run the logic once when the component mounts
    updateHandler();

    window.addEventListener("urlEndpointsLengthUpdated", updateHandler);

    return () => {
      window.removeEventListener("urlEndpointsLengthUpdated", updateHandler); // Cleanup
    };
  }, []);

  // sets initial form state based on route
  useEffect(() => {
    if (routerLocation.state && routerLocation.state.formValues) {
      setInitialValues(routerLocation.state.formValues)
      return
    }

    if (!urlEndpoint) {
      setInitialValues({
        ...initialUrlEndpointsFormState,
        preferenceList: initialSourceIds,
      })
      return
    }

    const { urlPrefix, desc, urlRewriter, urlFunction } = urlEndpoint
    let title, version;
    if(urlFunction){
      title = urlFunction.split('::')[0];
      version = parseInt(urlFunction.split('::')[1], 10);
    }
    setInitialValues({
      ...initialValues,
      urlPrefix,
      desc,
      preferenceList: initialSourceIds,
      urlRewriter: urlRewriter?.id,
      cloudinaryKeepAssetDeliveryTypes:
        urlRewriter?.cloudinaryKeepAssetDeliveryTypes,
      cloudinaryStrictMode: urlRewriter?.cloudinaryStrictMode,
      enableURLRewriter: urlRewriter?.id ? true : false,
      ...(title && version
        ? {
            urlEndpointFunctionTitle: title,
            urlEndpointFunctionVersion: version,
            enableUrlEndpointFunction: true,
          }
        : {
            enableUrlEndpointFunction: false,
            urlEndpointFunctionVersion: undefined,
            urlEndpointFunctionTitle: undefined,
          }),
    })
  }, [urlEndpoint, initialSourceIds, routerLocation])

  // builds a json mapping of {sourceId => sourceObject}
  useEffect(() => {
    const newSourcesJson = {}
    allSources?.forEach(element => {
      newSourcesJson[`${element.id}`] = element
    })
    setSourcesJson(newSourcesJson)
  }, [allSources])

  // builds initial preference list of origin IDs for current URL endpoint
  useEffect(() => {
    if (Object.keys(sourcesJson).length === 0) {
      return
    }

    if (!urlEndpoint) {
      const [defaultSourceId] = Object.keys(sourcesJson).filter(
        sourceId => sourcesJson[sourceId].isDefault,
      )
      setInitialSourceIds([defaultSourceId])
      return
    }

    const newInitialSourceIds: string[] = []
    let i = 1
    while (`${i}sourcePreference` in urlEndpoint) {
      const key = `${i}sourcePreference`
      if (
        urlEndpoint[key] &&
        urlEndpoint[key] !== '-1' &&
        urlEndpoint[key] in sourcesJson
      ) {
        newInitialSourceIds.push(urlEndpoint[key])
      }
      ++i
    }
    setInitialSourceIds(newInitialSourceIds)
  }, [urlEndpoint, sourcesJson])

  // add origin - POST '/api/clients/${user.clientNumber}/url-patterns'
  const addNewUrlEndpoint = useCallback(
    async ({ payload }: { payload: AddEndpointProps }) => {
      if (user?.clientNumber) {
        const resp = await request.post(
          `/${user.clientNumber}/url-patterns`,
          payload,
        )
        if (request.error) {
          toast({
            description: t(
              resp.reason
            ),
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        } else {
          toast({
            description: t('endpoint_form_success', {
              action: 'added',
            }),
            status: 'success',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
          refreshUrlEndpoints()
          if (resp.createdEndpoint) {
            routerHistory.push(
              `/url-endpoints/${
                (resp.createdEndpoint as unknown as UrlEndpointsResponse).id
              }`,
            )
          } else {
            routerHistory.push('/url-endpoints/')
          }
        }
      }
    },
    [urlEndpointId, initialValues],
  )

  // update origin - PUT '/api/clients/${user.clientNumber}/url-patterns/:sourceId'
  const updateUrlEndpoint = useCallback(
    async ({
      payload,
      values,
    }: {
      payload: UrlEndpointsResponse
      values: FormProps
    }) => {
      if (user?.clientNumber) {
        await request.put(
          `/${user.clientNumber}/url-patterns/${encodeHash(urlEndpointId)}`,
          payload,
        )

        if (request.error) {
          toast({
            description: t(
              'something_went_wrong',
              'Something went wrong. Please try again.',
            ),
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        } else {
          setInitialValues({
            ...initialValues,
            ...values,
          })
          refreshUrlEndpoints()
          toast({
            description: t('endpoint_form_success', {
              action: 'updated',
            }),
            status: 'success',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        }
      }
    },
    [urlEndpointId, initialValues],
  )

  // delete origin - DELETE '/api/clients/${user.clientNumber}/url-patterns/:sourceId'
  const deleteUrlEndpoint = useCallback(async (): Promise<boolean> => {
    if (user?.clientNumber) {
      await request.del(`/${user.clientNumber}/url-patterns/${encodeHash(urlEndpointId)}`)
      if (request.error) {
        toast({
          description: t(
            'something_went_wrong',
            'Something went wrong. Please try again.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return false
      }
      toast({
        description: t('endpoint_form_success', {
          action: 'deleted',
        }),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      refreshUrlEndpoints()
      routerHistory.push('/url-endpoints')
      return true
    }
    return true
  }, [urlEndpointId, initialValues])

  const selectRefCallback = useCallback((element: HTMLSelectElement) => {
    if (addOriginMenuOpen === false) {
      element?.blur()
    } else if (addOriginMenuOpen === true) {
      element?.focus()
    }
  }, [addOriginMenuOpen])

  const redirectToCreateOrigin = useCallback(({ formValues }: { formValues: EndpointFormProps }) => {
    pushInBrowserHistory({
      routerHistory,
      routerLocation,
      path: '/url-endpoints/add-new-origin',
      state: {
        newEndpoint: !urlEndpointId,
        urlEndpointId,
        formValues: formValues,
      },
    })
  }, [urlEndpoint, urlEndpointId])

  if (loadingUrlEndpoints || loadingSources) {
    return (
      <Center height="100%">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="appPaleGrey"
          color="appBlue"
          size="xl"
        />
      </Center>
    )
  }

  return (
    <Formik<EndpointFormProps>
      enableReinitialize={true}
      validationSchema={
        urlEndpoint?.isDefault
          ? UrlEndpointsWithoutURLPrefixSchema
          : UrlEndpointsSchema
      }
      initialValues={initialValues}
      onSubmit={async (values, { resetForm }) => {
        let payload: AddEndpointProps | UpdateEndpointProps = {
          urlPrefix: '',
          desc: '',
        }
        if (urlEndpoint) {
          payload = urlEndpoint
        }

        // urlPrefix and desc are set here
        urlEndpointsFormFields.forEach(field => {
          payload[field.fieldName] = values[field.fieldName]
        })

        // source preference fields are set here
        values.preferenceList.forEach((sourceId, index) => {
          payload[`${index + 1}sourcePreference`] = sourceId
        })
        let i = values.preferenceList.length + 1
        if (urlEndpoint) {
          while (`${i}sourcePreference` in urlEndpoint) {
            payload[`${i}sourcePreference`] = '-1'
            ++i
          }
        }

        if (values.enableURLRewriter && values.urlRewriter) {
          payload.urlRewriter = {
            id: values.urlRewriter,
          }
          if (values.urlRewriter === 'CLOUDINARY') {
            if (!isUndefined(values.cloudinaryKeepAssetDeliveryTypes)) {
              payload.urlRewriter.cloudinaryKeepAssetDeliveryTypes =
                values.cloudinaryKeepAssetDeliveryTypes
            }

            if (
              !isUndefined(values.cloudinaryStrictMode)
            ) {
              payload.urlRewriter.cloudinaryStrictMode =
                values.cloudinaryStrictMode
            }
          }
          if (urlEndpoint) payload.enableURLRewriter = values.enableURLRewriter
        } else {
          if (urlEndpoint) payload.enableURLRewriter = values.enableURLRewriter
          if (payload.urlRewriter) delete payload.urlRewriter
        }

        if (
          values.enableUrlEndpointFunction &&
          values.urlEndpointFunctionTitle &&
          values.urlEndpointFunctionVersion
        ) {
          payload.urlEndpointFunctionTitle = values.urlEndpointFunctionTitle
          payload.urlEndpointFunctionVersion = values.urlEndpointFunctionVersion
          payload.enableUrlEndpointFunction = true
        } else if (!values.enableUrlEndpointFunction) {
          payload.enableUrlEndpointFunction = false
        } else if (
          !values.urlEndpointFunctionTitle ||
          !values.urlEndpointFunctionVersion
        ) {
          payload.enableUrlEndpointFunction = false
        }
        if (urlEndpointId && urlEndpointId !== 'new') {
          await updateUrlEndpoint({ payload, values } as {
            payload: UrlEndpointsResponse
            values: FormProps
          })
        } else {
          await addNewUrlEndpoint({ payload })
        }
      }}
    >
      {({ isSubmitting, resetForm, dirty, isValid, values, handleChange }) => (
        <React.Fragment>
          <FormDirtyPrompt isDeleting={isOpenDelete} />
          <Form>
            <ActionBar
              urlEndpoint={urlEndpoint}
              isSubmitting={isSubmitting}
              onOpenDelete={onOpenDelete}
              resetForm={resetForm}
              dirty={dirty}
              isFormValid={isValid}
            />

            <MainContentWrapper>
              <Stack direction={['column']} spacing="2.5em">
                {!canAddMoreEndpoints &&
                  <UpgradePrompt message={`On your current plan, you can only have up to ${user?.pricingPlan?.limits?.fixed?.endpoints} URL-endpoint(s). Upgrade your account to add more endpoints.`} />
                }
                <Tabs
                  variant="soft-rounded"
                  size="md"
                  colorScheme="appBlue"
                  index={tabIndex}
                  onChange={setTabIndex}
                >
                  <TabList>
                    <Tab>{t('url_endpoint_configuration', 'Configuration')}</Tab>
                    <Tab> {t('advanced_settings', 'Advanced settings')} </Tab>
                    {urlEndpoint && (
                      <>
                        <Tab>
                          {t('url_endpoint_example_usage', 'Example usage')}
                        </Tab>
                      </>
                    )}
                  </TabList>
                  <TabPanels>
                    <TabPanel
                      style={{
                        padding: 0,
                        paddingTop: '1.5rem',
                      }}
                    >
                      <PermissionGuard
                        permission={PermissionsEnum.SELF_INTEGRATION_MANAGE}
                      >
                        {
                          (hasPermission: boolean) => (
                            <VStack spacing={10} alignItems="start">
                              {urlEndpointsFormFields.map(field => (
                                <UrlEndpointsTextField
                                  key={field.fieldName}
                                  name={field.fieldName}
                                  value={values[field.fieldName]}
                                  label={field.label}
                                  placeholder={field.placeholder}
                                  description={field.description({ identifier: values.urlPrefix })}
                                  handleChange={handleChange}
                                  required={
                                    field.required &&
                                    (!urlEndpoint?.isDefault ||
                                      field.fieldName !== 'urlPrefix')
                                  }
                                  isDisabled={
                                    !hasPermission ||
                                    (urlEndpoint?.isDefault
                                      ? field.fieldName === 'urlPrefix'
                                      : false
                                    )
                                  }
                                />
                              ))}

                              <FormLabel width="full" htmlFor="preferenceList">
                                <VStack spacing={4} alignItems="start" width="full">
                                  <Flex
                                    width="full"
                                    flexDirection="row"
                                    alignItems="center"
                                  >
                                    <ChakraText
                                      fontWeight="medium"
                                      color={colors.appDarkIndigo}
                                      fontSize="lg"
                                    >
                                      {t(
                                        'origin_preference',
                                        'Origin Preference',
                                      )}
                                    </ChakraText>

                                    <Spacer />

                                    {
                                      hasPermission ? (
                                        <Button
                                          variant="outline"
                                          marginLeft="1em"
                                          onClick={() => {
                                            openAddOriginMenu()
                                          }}
                                        >
                                          {t('attach_existing_origin_cta', 'Attach an existing origin')}
                                        </Button>
                                      ) : <></>
                                    }
                                  </Flex>

                                  {/* source preference list */}
                                  <PreferenceListItem
                                    source={defaultSourceJson}
                                    rank={1}
                                  />
                                  <Divider />
                                  <FormikField name="preferenceList">
                                    {() => (
                                      <React.Fragment>
                                        {values.preferenceList.length > 1 && (
                                          <DragDropList
                                            isDisabled={!hasPermission}
                                            spacing={8}
                                            currentItems={values.preferenceList.slice(
                                              1,
                                            )}
                                            setCurrentItems={newValue => {
                                              const newSourceIds = [
                                                values.preferenceList[0],
                                                ...newValue,
                                              ]
                                              handleChange({
                                                target: {
                                                  name: 'preferenceList',
                                                  value: newSourceIds,
                                                },
                                              })
                                            }}
                                            render={(sourceId, index) =>
                                              sourcesJson[sourceId] && (
                                                <PreferenceListItem
                                                  key={sourceId}
                                                  source={sourcesJson[sourceId]}
                                                  rank={Number(index) + 2}
                                                  handleRemoveSource={() => {
                                                    const newSourceIds = [
                                                      ...values.preferenceList,
                                                    ]
                                                    newSourceIds.splice(
                                                      newSourceIds.indexOf(
                                                        sourceId.toString(),
                                                      ),
                                                      1,
                                                    )
                                                    handleChange({
                                                      target: {
                                                        name: 'preferenceList',
                                                        value: newSourceIds,
                                                      },
                                                    })
                                                  }}
                                                />
                                              )
                                            }
                                          />
                                        )}

                                        {
                                          hasPermission ? (
                                            <HStack
                                              spacing={4}
                                              alignItems="center"
                                              width="full"
                                            >
                                              <Box width="6%" />
                                              <Box minWidth={0} flex={1}>
                                                <AddExistingSource
                                                  selectRefCallback={selectRefCallback}
                                                  sourcesJson={sourcesJson}
                                                  selectedSources={values.preferenceList}
                                                  addOriginMenuOpen={addOriginMenuOpen}
                                                  closeAddOriginMenu={closeAddOriginMenu}
                                                  openAddOriginMenu={openAddOriginMenu}
                                                  onCreateOriginClick={() => redirectToCreateOrigin({ formValues: values })}
                                                  handleChange={newOption => {
                                                    closeAddOriginMenu()
                                                    handleChange({
                                                      target: {
                                                        name: 'preferenceList',
                                                        value: [
                                                          ...values.preferenceList,
                                                          newOption?.value,
                                                        ],
                                                      },
                                                    })
                                                  }}
                                                />
                                              </Box>
                                            </HStack>
                                          ) : <></>
                                        }
                                      </React.Fragment>
                                    )}
                                  </FormikField>
                                </VStack>
                              </FormLabel>
                            </VStack>
                          )
                        }
                      </PermissionGuard>
                    </TabPanel>
                    <TabPanel
                      style={{
                        padding: 0,
                        paddingTop: '1.5rem',
                      }}
                    >
                      {urlEndpoint?.enableOriginCacheControl && (
                        <OriginCacheControlInfo enabled={urlEndpoint.enableOriginCacheControl} />
                      )}
                      <VStack spacing="0.75em">
                        <UrlRewriterForm  values={values} handleChange={handleChange} />
                        <UrlEndpointFunctionSelector values={values} urlEndpointFunctions={urlEndpointFunctions} />
                      </VStack>
                    </TabPanel>
                    {urlEndpoint && (
                      <TabPanel
                        style={{
                          padding: 0,
                          paddingTop: '1.5rem',
                        }}
                      >
                        <ExampleEndpointUsage urlPrefix={urlEndpoint.urlPrefix} />
                      </TabPanel>
                    )}
                  </TabPanels>
                </Tabs>
              </Stack>
            </MainContentWrapper>
          </Form>
          <DeleteModal
            isOpen={isOpenDelete}
            onClose={onCloseDelete}
            onSubmit={deleteUrlEndpoint}
            urlEndpointName={values.urlPrefix}
          />
        </React.Fragment>
      )}
    </Formik>
  )
}
